import React from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import infoicon from "../../assets/images/info-b.svg";

// Shared formula-text BUILDERS for columns that appear identically (same key, same backend
// source) across multiple reports — kept here once so the wording can never drift between
// reports. Each entry is a function of (startDateLabel, endDateLabel) — the report's
// currently-selected Period Start Date / Period End Date, already formatted as display
// strings (or a fallback phrase when no date is selected yet) — so the tooltip always shows
// the actual dates the user searched with, not a placeholder. Report-specific columns that
// only exist in one report should keep their own local formula map instead of being added
// here.
export const SHARED_COLUMN_FORMULAS = {
  wh_earning_itd: () =>
    "Add up (Nightly Revenue multiplied by the unit's FF&E Brokerage Fee Percent) for every reservation. If the unit has an FF&E Brokerage Fee Start Date set, only count reservations checked out on or after that date. If the unit has an FF&E Brokerage Fee End Date set, only count reservations checked out on or before that date.",
  wh_earning_period: (startDateLabel, endDateLabel) =>
    `Add up (Nightly Revenue multiplied by the unit's FF&E Brokerage Fee Percent) for every reservation checked out between ${startDateLabel} and ${endDateLabel}. If the unit has an FF&E Brokerage Fee Start Date set, only count reservations checked out on or after that date. If the unit has an FF&E Brokerage Fee End Date set, only count reservations checked out on or before that date.`,
  intro_earning_itd: () =>
    "Add up (Nightly Revenue multiplied by the unit's Introductory Brokerage Fee Percent) for every reservation. If the unit has an Introductory Brokerage Fee Start Date set, only count reservations checked out on or after that date. If the unit has an Introductory Brokerage Fee End Date set, only count reservations checked out on or before that date.",
  intro_earning_period: (startDateLabel, endDateLabel) =>
    `Add up (Nightly Revenue multiplied by the unit's Introductory Brokerage Fee Percent) for every reservation checked out between ${startDateLabel} and ${endDateLabel}. If the unit has an Introductory Brokerage Fee Start Date set, only count reservations checked out on or after that date. If the unit has an Introductory Brokerage Fee End Date set, only count reservations checked out on or before that date.`,
};

// Formats a JS Date (or falsy) into the plain-English phrase used inside a formula
// sentence — e.g. "09/01/2026", or a fallback phrase when no date has been selected yet.
// Report components pass their own date-formatting (they already use Moment for display
// elsewhere), this just supplies the "no date selected" fallback wording so every report
// using SHARED_COLUMN_FORMULAS phrases it the same way.
export const formatFormulaDateLabel = (formattedDateOrNull, fallbackPhrase) =>
  formattedDateOrNull ? formattedDateOrNull : fallbackPhrase;

// Column header label + hover-triggered info-icon tooltip showing the formula behind it -
// same OverlayTrigger/Popover pattern already used for icon tooltips elsewhere in this repo
// (e.g. PropertyDocuments.jsx's "Copy Path" icon), just with a formula body instead of a
// one-word confirmation. Takes the already-built formula text directly (not a lookup key),
// so it works the same whether the caller resolved it from SHARED_COLUMN_FORMULAS above or
// from its own report-local formula map/function — this is the reusable piece the next
// report's rollout should import as-is.
export const withFormulaTooltip = (label, formula) => {
  if (!formula) {
    return label;
  }
  return (
    <span className="wh-header-with-info d-inline-flex align-items-center justify-content-center">
      {label}
      <OverlayTrigger
        trigger="hover"
        placement="top"
        rootClose
        overlay={
          <Popover className="wh-formula-popover">
            <Popover.Title as="h3" className="p-0">
              {label}
            </Popover.Title>
            <Popover.Content>{formula}</Popover.Content>
          </Popover>
        }
      >
        <img
          src={infoicon}
          className="mg-l-5 hand-cursor wh-formula-icon"
          alt="Formula info"
          onClick={(e) => e.stopPropagation()}
        />
      </OverlayTrigger>
    </span>
  );
};
